import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { DialogType, PrimaryButton } from 'office-ui-fabric-react';
import isEmpty from 'lodash/isEmpty';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import PageTitle from '../../components/PageTitle';
import styles from './AddAdvanceGroup.style';
import Module from '../../components/Module';
import Main from '../../components/Main';
import TextButton from '../../components/TextButton';
import AdvanceGroupForm from '../../components/AdvanceGroupForm';
import SavingIndicator from '../../components/SavingIndicator';
import useAPI from '../../hooks/useAPI';
import { fetchAdvanceGroupDetails } from '../../actions/advanceGroups';
import PauseTransactionsDialog from '../../components/PauseTransactionsDialog';

const AddAdvanceGroup = ({ title, mode }) => {
  const { advancegroupId } = useParams();
  const [isSaving, setisSaving] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const history = useHistory();
  const formRef = useRef();

  const handleCancel = () => {
    history.goBack();
  };

  const handleFormSubmission = async () => {
    await formRef.current.submitForm();
    if (formRef.current && !isEmpty(formRef.current.errors)) {
      setisSaving(false);
      setIsDialogVisible(false);
    }
  };

  const handleSubmitFromDialog = async () => {
    setisSaving(true);
    formRef.current.setFieldValue('submitButtonType', 'Save');
    await handleFormSubmission();
  };

  const handleSave = async () => {
    // show pause contract dialog only if associated contract is approved
    if (formRef.current.values?.contract?.approvalStatus?.toLowerCase() === 'approved') {
      setIsDialogVisible(true);
    } else {
      await handleSubmitFromDialog();
    }
  };

  const handleFailure = () => {
    setisSaving(false);
  };

  const handleSuccess = async newAdvanceGroup => {
    setisSaving(false);
    history.push(`/advancegroups/${newAdvanceGroup._id}`);
  };

  let isLoading = false;

  if (advancegroupId)
    ({ isLoading } = useAPI(() => fetchAdvanceGroupDetails(advancegroupId), [advancegroupId]));

  if (isLoading) {
    return (
      <Spinner
        data-testid="addAdvanceGroupsSpinner"
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
      />
    );
  }

  return (
    <>
      <PageTitle>{title}</PageTitle>
      <Main>
        <Module data-testid="advanceGroupFormId">
          <AdvanceGroupForm
            onSuccess={handleSuccess}
            onFailure={handleFailure}
            ref={formRef}
            advanceGroupId={advancegroupId}
            mode={mode}
          />
        </Module>

        <div className={styles.buttonContainer}>
          <TextButton className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </TextButton>
          {!isSaving ? (
            <PrimaryButton
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isSaving}
              data-testid="AdvanceGroupFormSaveDraft"
            >
              Save
            </PrimaryButton>
          ) : (
            <SavingIndicator data-testid="saveLoader" className={styles.saveButton} />
          )}
        </div>

        <PauseTransactionsDialog
          onDismiss={() => setIsDialogVisible(false)}
          onSubmit={handleSubmitFromDialog}
          hidden={!isDialogVisible}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Edits to group assignment may cause reprocessing',
            closeButtonAriaLabel: 'Close',
          }}
          isAdvanceGroup
        />
      </Main>
    </>
  );
};

AddAdvanceGroup.propTypes = {
  title: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(Object.values(AdvanceGroupForm.MODE)).isRequired,
};

export default AddAdvanceGroup;
